import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { colors } from '../../../../../colorVars';
import { addSymbol } from '../../../../../stores/websocketStore/utils/outgoingMessages/symbols/addSymbol';
import { removeSymbol } from '../../../../../stores/websocketStore/utils/outgoingMessages/symbols/removeSymbol';
import { useWebsocketStore } from '../../../../../stores/websocketStore/websocketProvider';
import { useAlertsStore } from '../../../../../stores/websocketStore/_alertsStore';
import CloseWindowButton from '../../../../atomic/buttons/closeWindow/CloseWindowButton';
import { Body2, fontSizes } from '../../../../atomic/text/Text';
import { groupNumberMap } from '../alertWatchlistComponent/groupNumberMap';
import style from './AlertChartComponent.module.css';
import { AlertChartWrapper } from './AlertChartWrapper';

export const AlertChartComponent = observer(
  ({ details, moduleId, layoutType }) => {
    const storedSymbol = details.chartData?.symbol ?? null;
    const storedCandleDuration = details.chartData?.candleDuration ?? null;
    const storedTimeUnit = details.chartData?.timeUnit ?? null;
    const storedNumberOfCandles = details.chartData?.numberOfCandles ?? null;
    const storedShowCandleNumbers =
      details.chartData?.showCandleNumbers ?? null;
    const storedWidth = details.width;
    const storedHeight = details.height;
    const { layoutStore } = useWebsocketStore();
    const { dataSource } = useAlertsStore();

    const widthRef = useRef(null);
    const groupNumber = details?.groupNumber || 0;
    const removeModule = () => {
      layoutStore.removeModuleFromCurrentLayout(layoutType, moduleId);
    };
    const [symbol, setSymbol] = useState(null);
    const [previousSymbol, setPreviousSymbol] = useState(null);
    const [previousDataSource, setPreviousDataSource] = useState(null);
    const [candleDuration, setCandleDuration] = useState(null);
    const [timeUnit, setTimeUnit] = useState(null);
    const [numberOfCandles, setNumberOfCandles] = useState(null);
    const [showCandleNumbers, setShowCandleNumbers] = useState(null);
    const setters = {
      setSymbol,
      setCandleDuration,
      setTimeUnit,
      setNumberOfCandles,
      setShowCandleNumbers,
    };

    // watch group selected data, and when it changes, set the chart parameters to this data
    const symbolDetails = layoutStore.groupSelectedData[groupNumber] || {};
    const detailsSymbol = symbolDetails?.symbol ?? null;

    useEffect(() => {
      if (
        symbol &&
        candleDuration &&
        timeUnit &&
        numberOfCandles &&
        showCandleNumbers !== null
      ) {
        const chartDetailsObject = {
          symbol,
          candleDuration,
          timeUnit,
          numberOfCandles,
          showCandleNumbers,
        };
        layoutStore.updateCurrentLayoutChartModuleData(
          'alerts',
          moduleId,
          chartDetailsObject
        );
        layoutStore.updateServerCurrentLayout('alerts', 'currentAlerts');
      }
    }, [symbol, candleDuration, timeUnit, numberOfCandles, showCandleNumbers]);

    useEffect(() => {
      if (storedSymbol) setSymbol(storedSymbol);
      else setSymbol('SPY');
      if (storedCandleDuration) setCandleDuration(storedCandleDuration);
      else setCandleDuration(1);
      if (storedTimeUnit) setTimeUnit(storedTimeUnit);
      else setTimeUnit('MINUTES');
      if (storedNumberOfCandles) setNumberOfCandles(storedNumberOfCandles);
      else setNumberOfCandles(30);
      if (storedShowCandleNumbers !== null)
        setShowCandleNumbers(storedShowCandleNumbers);
      else setShowCandleNumbers(true);
    }, [
      storedSymbol,
      storedCandleDuration,
      storedTimeUnit,
      storedNumberOfCandles,
      storedShowCandleNumbers,
    ]);

    useEffect(() => {
      if (detailsSymbol) setSymbol(detailsSymbol);
      // if (detailsCandleDuration) setCandleDuration(detailsCandleDuration);
      // if (detailsTimeUnit) setTimeUnit(detailsTimeUnit);
    }, [detailsSymbol]);

    useEffect(() => {
      addSymbol({
        symbol,
        dataSource,
        watchlist_name: '__' + moduleId,
      });
      if (previousSymbol) {
        removeSymbol({
          symbol: previousSymbol,
          dataSource,
          watchlist_name: '__' + moduleId,
        });
      }
      setPreviousSymbol(symbol);
      return () => {
        removeSymbol({
          symbol,
          dataSource,
          watchlist_name: '__' + moduleId,
        });
      };
    }, [symbol]);

    useEffect(() => {
      addSymbol({
        symbol,
        dataSource,
        watchlist_name: '__' + moduleId,
      });
      if (previousSymbol) {
        removeSymbol({
          symbol,
          dataSource: previousDataSource,
          watchlist_name: '__' + moduleId,
        });
      }
      setPreviousDataSource(dataSource);
      return () => {
        removeSymbol({
          symbol,
          dataSource,
          watchlist_name: '__' + moduleId,
        });
      };
    }, [dataSource]);

    return (
      <div
        className={style.container}
        onClick={() => layoutStore.setCurrentFocusedModule(moduleId)}
      >
        <header className={style.header}>
          <div className={style.group}>
            <Body2 margin="0 5px 0 0" color={colors.lightGrey} fontWeight={700}>
              Chart
            </Body2>
            <select
              value={groupNumber}
              onChange={({ target }) => {
                layoutStore.updateCurrentLayoutModuleGroupNumber(
                  'alerts',
                  moduleId,
                  target.value
                );
                layoutStore.updateServerCurrentLayout(
                  'alerts',
                  'currentAlerts'
                );
              }}
              className={style.groupSelect}
              style={{
                backgroundColor: groupNumberMap[groupNumber].color,
                color: groupNumber === '6' ? colors.black : colors.lightGrey,
                border: 'none',
                fontSize: fontSizes.body4,
                fontWeight: 700,
              }}
            >
              {groupNumberMap.map(({ value, label, color }) => (
                <option
                  key={value}
                  value={value}
                  style={{ backgroundColor: color }}
                >
                  {label}
                </option>
              ))}
            </select>
          </div>
          <CloseWindowButton
            width={15}
            stroke={2}
            color={colors.red}
            handleClick={removeModule}
          />
        </header>
        {symbol &&
        candleDuration &&
        timeUnit &&
        numberOfCandles &&
        showCandleNumbers !== null ? (
          <div className={style.body} ref={widthRef}>
            <AlertChartWrapper
              width={storedWidth || null}
              height={storedHeight || null}
              symbol={symbol}
              timeUnit={timeUnit}
              candleDuration={candleDuration}
              numberOfCandles={numberOfCandles}
              showCandleNumbers={showCandleNumbers}
              setters={setters}
              dataSource={dataSource}
            />
          </div>
        ) : null}
      </div>
    );
  }
);
